/**
 * DeleteRecurrenceDialog - Dialog para escolher como excluir lançamento recorrente
 * 
 * Oferece 3 opções:
 * 1. Apenas este lançamento
 * 2. Este e os próximos lançamentos
 * 3. Todos os lançamentos desta série
 */

import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { Label } from '../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import { Trash2, AlertTriangle } from 'lucide-react';
import { formatCurrency } from '../../../lib/utils/format';
import { cn } from '../../../components/ui/utils';
import type { CashFlowEntry } from '../../../types/database';

export type DeleteOption = 'single' | 'future' | 'all';

interface DeleteRecurrenceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction: CashFlowEntry;
  seriesCount: {
    total: number;
    future: number;
  };
  seriesTotals: {
    total: number;
    future: number;
  };
  onConfirm: (option: DeleteOption) => void;
  isDeleting?: boolean;
}

export function DeleteRecurrenceDialog({
  open,
  onOpenChange,
  transaction,
  seriesCount,
  seriesTotals,
  onConfirm,
  isDeleting = false,
}: DeleteRecurrenceDialogProps) {
  const [selectedOption, setSelectedOption] = useState<DeleteOption>('single');

  const handleConfirm = () => {
    onConfirm(selectedOption);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-destructive/10 rounded-lg">
              <Trash2 className="h-5 w-5 text-destructive" />
            </div>
            <div className="flex-1">
              <AlertDialogTitle className="text-lg">
                Excluir Lançamento Recorrente
              </AlertDialogTitle>
            </div>
          </div>
          <AlertDialogDescription className="text-sm text-muted-foreground pt-2">
            Este lançamento faz parte de uma série recorrente. Como deseja prosseguir?
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* Transaction Info */}
        <div className="p-3 bg-muted/30 rounded-lg space-y-1">
          <p className="text-sm font-medium">{transaction.description}</p>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{transaction.frequency === 'monthly' ? 'Mensal' : 
                   transaction.frequency === 'weekly' ? 'Semanal' : 
                   transaction.frequency === 'daily' ? 'Diário' :
                   transaction.frequency === 'quarterly' ? 'Trimestral' : 'Anual'}</span>
            <span className="font-medium text-foreground">
              {formatCurrency(transaction.amount, transaction.currency)}
            </span>
          </div>
        </div>

        {/* Options */}
        <RadioGroup
          value={selectedOption}
          onValueChange={(value) => setSelectedOption(value as DeleteOption)}
          className="space-y-3"
        >
          {/* Option 1: Single */}
          <label
            htmlFor="option-single"
            className={cn(
              'flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all',
              selectedOption === 'single'
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-muted-foreground/50'
            )}
          >
            <RadioGroupItem value="single" id="option-single" className="mt-0.5" />
            <div className="flex-1 space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Apenas este lançamento</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Os demais lançamentos da série serão mantidos
              </p>
            </div>
          </label>

          {/* Option 2: Future */}
          <label
            htmlFor="option-future"
            className={cn(
              'flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all',
              selectedOption === 'future'
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-muted-foreground/50'
            )}
          >
            <RadioGroupItem value="future" id="option-future" className="mt-0.5" />
            <div className="flex-1 space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Este e os próximos</span>
                <span className="text-xs font-medium text-muted-foreground">
                  {seriesCount.future} {seriesCount.future === 1 ? 'lançamento' : 'lançamentos'}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Exclui este e todos os futuros desta série
              </p>
              {seriesTotals.future > 0 && (
                <p className="text-xs font-medium text-destructive">
                  Impacto: {formatCurrency(seriesTotals.future, transaction.currency)}
                </p>
              )}
            </div>
          </label>

          {/* Option 3: All */}
          <label
            htmlFor="option-all"
            className={cn(
              'flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all',
              selectedOption === 'all'
                ? 'border-destructive bg-destructive/5'
                : 'border-border hover:border-muted-foreground/50'
            )}
          >
            <RadioGroupItem value="all" id="option-all" className="mt-0.5" />
            <div className="flex-1 space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Todos os lançamentos</span>
                <span className="text-xs font-medium text-muted-foreground">
                  {seriesCount.total} {seriesCount.total === 1 ? 'lançamento' : 'lançamentos'}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Exclui toda a série recorrente
              </p>
              {seriesTotals.total > 0 && (
                <p className="text-xs font-medium text-destructive">
                  Impacto: {formatCurrency(seriesTotals.total, transaction.currency)}
                </p>
              )}
            </div>
          </label>
        </RadioGroup>

        {/* Warning */}
        <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-900 dark:text-amber-100">
            <strong>Atenção:</strong> Esta ação não pode ser desfeita. 
            {selectedOption === 'all' && ' Todos os lançamentos da série serão excluídos permanentemente.'}
            {selectedOption === 'future' && ' Este e todos os lançamentos futuros serão excluídos permanentemente.'}
            {selectedOption === 'single' && ' Este lançamento será excluído permanentemente.'}
          </p>
        </div>

        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel disabled={isDeleting}>
            Cancelar
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isDeleting}
            className={cn(
              'bg-destructive hover:bg-destructive/90',
              isDeleting && 'opacity-50 cursor-not-allowed'
            )}
          >
            {isDeleting ? 'Excluindo...' : 'Excluir'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

